import React, { useState } from 'react';
import { XCircle, MapPin, Video } from 'lucide-react';
import { cancelAppointment, STATUS } from '../lib/appointments';
import { hoursLeft, isCharged } from '../lib/cancellation';
import { t, formatDate, formatTime, formatMoney } from '../i18n';
import { Modal, Button, Field, Input, Alert } from './ui';

/**
 * A patient cancelling one of their family's sessions, or withdrawing a request
 * still waiting for approval (migration 0057). Opened from Próximos in Inicio
 * and from the week in Reservar turno.
 *
 * The patient gives notice as they confirm, so whether it is charged is known
 * before they do: the professional's cutoff comes with the session
 * (my_sessions), and cancel_my_appointment decides again with the same rule.
 * The amount comes only from professionals who show payments in the app.
 */

const PatientCancelModal = ({ session, professionalName, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const request = session.status === STATUS.REQUESTED;
  const policy = session.cancellation_notice_hours ?? null;
  const now = Date.now();
  const left = hoursLeft(session.starts_at, now);
  const charged = !request && isCharged({
    policyHours: policy, by: 'patient', startsAt: session.starts_at, noticeAt: now,
  });
  const amount = session.late_cancellation_amount;
  const when = `${formatDate(session.starts_at, { weekday: 'long', day: 'numeric', month: 'long' })}, ${formatTime(session.starts_at)}`;
  const person = `${session.first_name ?? ''} ${session.last_name ?? ''}`.trim();

  let terms = null;
  if (request) {
    terms = t('patientCancel.requestFree');
  } else if (policy) {
    const lead = left < 1
      ? t('patientCancel.lessThanAnHour', { policy })
      : t('patientCancel.hoursLeft', { count: left, policy });
    const tail = !charged
      ? t('patientCancel.free')
      : amount != null
        ? t('patientCancel.chargedAmount', { amount: formatMoney(amount) })
        : t('patientCancel.charged');
    terms = `${lead} ${tail}`;
  }

  const confirm = async () => {
    setBusy(true);
    setError(null);
    const { error: cancelError } = await cancelAppointment(session.id, reason.trim() || null);
    setBusy(false);
    if (cancelError) { setError(cancelError.message); return; }
    onDone?.(t(request ? 'patientCancel.withdrawn' : 'patientCancel.cancelled', { when }));
  };

  return (
    <Modal
      open
      title={t(request ? 'patientCancel.withdrawTitle' : 'patientCancel.title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.back')}</Button>
          <Button variant="danger" onClick={confirm} loading={busy}>
            <XCircle size={16} />{t(request ? 'patientCancel.withdraw' : 'patientCancel.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-slate-50 p-4 text-sm">
          <p className="font-semibold text-slate-900">{person}</p>
          <p className="text-slate-700 first-letter:uppercase">{when}</p>
          <p className="mt-1 flex flex-wrap items-center gap-1 text-slate-600">
            {professionalName}
            {(session.is_online || session.location_name) && (
              <>
                <span aria-hidden="true">·</span>
                {session.is_online ? <Video size={12} /> : <MapPin size={12} />}
                {session.is_online ? t('availability.online') : session.location_name}
              </>
            )}
          </p>
        </div>

        {error && <Alert tone="danger">{error}</Alert>}
        {terms && <Alert tone={charged ? 'warning' : 'info'}>{terms}</Alert>}

        <Field label={t('agenda.cancelReason')}>
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('patientCancel.reasonPlaceholder')}
          />
        </Field>
        <p className="text-xs text-slate-500">
          {t('patientCancel.tellsProfessional', { professional: professionalName })}
        </p>
      </div>
    </Modal>
  );
};

export default PatientCancelModal;
